import { db } from "../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";
import useShopStore from "../store/useShopStore";

// -------- utils ----------
export const priceToNumber = (val) => {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  return parseFloat(String(val).replace(",", ".").trim()) || 0;
};

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Cache is per-shop: Map<shopId, Map<extraId, extra>>
const extrasCacheByShop = new Map();

// Optional: call this if you ever force-switch shops at runtime
export function clearExtrasCacheForShop(shopId) {
  if (!shopId) return;
  extrasCacheByShop.delete(shopId);
}

/**
 * Fetch extras by IDs for the CURRENT shop (from Zustand).
 * - Reads shopId from useShopStore
 * - Chunks Firestore documentId() "in" queries by 10
 * - Caches results per shop
 */
export async function fetchExtrasByIds(ids = []) {
  const { shop } = useShopStore.getState();
  const shopId = shop?.id;

  const uniqueIds = Array.from(new Set(ids)).filter(Boolean);
  if (!shopId || uniqueIds.length === 0) return [];

  // ensure cache map exists for this shop
  if (!extrasCacheByShop.has(shopId)) {
    extrasCacheByShop.set(shopId, new Map());
  }
  const shopCache = extrasCacheByShop.get(shopId);

  const toFetch = [];
  const fromCache = [];

  for (const id of uniqueIds) {
    if (shopCache.has(id)) fromCache.push(shopCache.get(id));
    else toFetch.push(id);
  }
  if (toFetch.length === 0) return fromCache;

  const colRef = collection(db, "extras");
  const fetched = [];
  const chunks = chunk(toFetch, 10); // Firestore "in" limit

  for (const idsChunk of chunks) {
    const q = query(
      colRef,
      where("shopId", "==", shopId),
      where(documentId(), "in", idsChunk)
    );
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = { id: d.id, ...d.data() };
      data._priceNumber = priceToNumber(data.price);
      shopCache.set(d.id, data);
      fetched.push(data);
    });
  }

  return [...fromCache, ...fetched];
}

export function buildExtrasMap(extrasArray = []) {
  const map = {};
  for (const ex of extrasArray) map[ex.id] = ex;
  return map;
}
